import React, {useState} from 'react';
import Form from 'react-bootstrap/Form';
import Button from '@/components/ui/Button';
import axios from 'axios';
import * as yup from 'yup';
import {useFormik} from 'formik';
import {useStoreActions} from 'easy-peasy';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import {useAlert} from '@/components/alerts';
import useData from '@/utils/useData';
import CellLoader from '@/components/loading/CellLoader';  
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import AsyncSelect from 'react-select/async';
import i18n from 'i18n';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {RestrictedFieldInput} from '@/components/utils/DataNotAvailable';

interface Agent {
    id: number;
    name: string;
    externalId: string;
}

interface AgentOption {
    label: string;
    value: number;
}

interface TeamAssignmentSummary {
    teamId: number;
    teamName: string;
    teamShortname: string;
    isActive: boolean;
}

interface AgentTeamAssignmentsResponse {
    teamAssignments: TeamAssignmentSummary[];
}

interface AgentTeamSettings {
    agentId: number;
    teamId: number;
    activeBetween: {
        start: string;
        end: string;
    };
}

interface AgentTeamSettingsFormProps {
    agentId?: number;
    teamId?: number;
    onSave?: () => void;
}

interface ApiResponse {
    data: {
        items: Agent[];
    };
}

interface AssignAgentTeamResponse {
    data: Agent;
}

const AgentTeamSettingsFormSchema = yup.object({
    agentId: yup.number().required(i18n.t('jt_ag_agent_required.text')),
    teamId: yup.number().positive().integer(),
    activeBetween: yup.object({
        start: yup.date()
            .required(i18n.t('jt_ag_start_date_required.text'))
            .test('startBeforeEnd', i18n.t('jt_ag_start_before_end.text'), function (value) {
                const {end} = this.parent;
                return !end || value <= end;
            }),
        end: yup.date()
            .nullable()
            .min(yup.ref('start'), i18n.t('jt_ag_end_after_start.text')),
    }).required(i18n.t('jt_ag_date_range_required.text')),
});

const loadAgentsRequest = (inputValue: string): Promise<ApiResponse> => axios.get(window.URLS.agentsResource, {
    params: {
        query: inputValue,
    },
})
    .catch((error) => {
        if (error.response.status === 400) {
            return error.response;
        }

        throw error;
    });
const loadAgentsRequestDebounced = AwesomeDebouncePromise(loadAgentsRequest, 280);

const AssignAgentTeamSettingsForm: React.FC<AgentTeamSettingsFormProps> = React.memo((
    {
        agentId = 0,
        teamId = 0,
        onSave = () => {},
    },
) => {
    const {showSuccess} = useAlert();
    const {userData} = useOptimizedConfig();
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const agentRestricted = restrictedFieldKeys.includes('agent_pii');
    const [agentHasAnyTeam, setAgentHasAnyTeam] = useState<boolean | null>(null);
    const [checkingAssignments, setCheckingAssignments] = useState(false);
    const [agentTeamAssignments, setAgentTeamAssignments] = useState<TeamAssignmentSummary[]>([]);

    const getAgentOptions = async (inputValue: string): Promise<AgentOption[]> => {
        const results = await loadAgentsRequestDebounced(inputValue);
        return results.data.items.map((agent) => ({
            label: `${agent.name || ''} (${agent.externalId || ''})`.trim() || `Agent #${agent.id}`,
            value: agent.id,
        }));
    };

    let agentDetailUrl = '';

    // Note: This form appears to be for creating new assignments, not editing existing ones.
    // The assignAgentToTeamResourceDetail URL requires agent_id, team_id, AND agent_team_id.
    // Since we don't have agent_team_id in this context, we skip the data fetching.
    // This suggests the original code had a bug - it was trying to use a 3-parameter URL with only 2 parameters.
    if (agentId && teamId) {
        // TODO: Determine if we need a different URL pattern for this use case
        // or if this data fetching should be removed entirely for new assignments
        agentDetailUrl = ''; // Intentionally empty - see comment above
    }

    const [selectedAgent, loadingAgentData] = useData<AgentTeamSettings>(agentDetailUrl, {useLocalStore: true});

    const setAgent = useStoreActions((actions: any) => actions.agents.addOrEdit);

    const loading = agentId && loadingAgentData;

    const checkAgentTeamAssignments = async (selectedAgentId: number) => {
        if (!selectedAgentId) {
            setAgentHasAnyTeam(null);
            setAgentTeamAssignments([]);
            return;
        }

        try {
            setCheckingAssignments(true);
            const template = window.URLS?.agentTeamAssignments || '/internal_api/v1/agents/:agent_id/team_assignments';
            const url = template.replace(':agent_id', String(selectedAgentId));
            const {data} = await axios.get<AgentTeamAssignmentsResponse>(url);
            const assignments = Array.isArray(data.teamAssignments) ? data.teamAssignments : [];

            // Only keep active assignments (agent is currently in those teams)
            const activeAssignments = assignments.filter((assignment) => assignment.isActive);
            const hasAnyAssignments = activeAssignments.length > 0;
            setAgentHasAnyTeam(hasAnyAssignments);
            setAgentTeamAssignments(activeAssignments);
        } catch (e) {
            // If the check fails, fall back to "unknown" state
            setAgentHasAnyTeam(null);
            setAgentTeamAssignments([]);
        } finally {
            setCheckingAssignments(false);
        }
    };

    const onSubmit = async (values: AgentTeamSettings) => {
        let response: AssignAgentTeamResponse;
        let requestMethod: (url: string, payload: any) => Promise<AssignAgentTeamResponse>;
        const url = window.URLS.assignAgentToTeamResource;
        let actionName: string;

        const payload = {
            team_id: parseInt(values.teamId.toString(), 10),
            agent_id: parseInt(values.agentId.toString(), 10),
            active_between: `${values.activeBetween.start},${values.activeBetween.end}`
        };

        if (agentId) {
            requestMethod = axios.put;
            actionName = 'changed';
        } else {
            requestMethod = axios.post;
            actionName = 'created';
        }

        try {
            response = await requestMethod(url, payload);
        } catch (e: any) {
            if (e.response.status === 400) {
                formik.setFieldError('activeBetween.start', e.response?.data?.message ?? i18n.t('invalid_data.text'));
                return;
            }
            throw e;
        }
        onSave();

        setAgent(response.data);
        showSuccess(i18n.t('jt_st_agent_team_success_action.text', {actionName}));
    };

    const formik = useFormik<AgentTeamSettings>({
        initialValues: {
            agentId: selectedAgent?.agentId || agentId || 0,
            teamId: selectedAgent?.teamId || teamId,
            activeBetween: {
                start: selectedAgent?.activeBetween?.start || '',
                end: selectedAgent?.activeBetween?.end || ''
            }
        },
        validationSchema: AgentTeamSettingsFormSchema,
        enableReinitialize: true,
        onSubmit,
    });

    if (loading) {
        return (<CellLoader />);
    }

    const currentTeamAssignment = teamId
        ? agentTeamAssignments.find((assignment) => assignment.teamId === teamId)
        : undefined;

    const otherTeamAssignments = teamId
        ? agentTeamAssignments.filter((assignment) => assignment.teamId !== teamId)
        : agentTeamAssignments;

    return (
        <>
            <Form noValidate onSubmit={formik.handleSubmit}>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="agentId" data-cy="select-agent" >
                            <Form.Label>{i18n.t('jt_select_agent.text')}</Form.Label>
                            {agentRestricted ? (
                                <RestrictedFieldInput />
                            ) : (
                                <AsyncSelect<AgentOption>
                                    isClearable={false}
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={getAgentOptions}
                                    hideSelectedOptions={false}
                                    onChange={async (v) => {
                                        const value = v?.value || 0;
                                        formik.setFieldValue('agentId', value);
                                        await checkAgentTeamAssignments(value);
                                    }}
                                    onBlur={formik.handleBlur}
                                    aria-label="select-agent"
                                />
                            )}
                            {checkingAssignments && (
                                <Form.Text muted>
                                    {i18n.t('jt_loading_data_general.text')}
                                </Form.Text>
                            )}
                            {!checkingAssignments && agentHasAnyTeam && (
                                <>
                                    {currentTeamAssignment && (
                                        <Form.Text className="text-warning d-block">
                                            {i18n.t('jt_ag_assigned_to_this_team.text', 'This agent is currently assigned to this team.')}
                                        </Form.Text>
                                    )}
                                    {otherTeamAssignments.length > 0 && (
                                        <Form.Text className="text-muted d-block">
                                            {i18n.t('jt_ag_assigned_to_other_teams.text', 'This agent is currently assigned to:')} {''}
                                            {otherTeamAssignments.map((assignment) => assignment.teamName).join(', ')}
                                        </Form.Text>
                                    )}
                                </>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="startDate" >
                            <Form.Label>{i18n.t('jt_started_team.text')}</Form.Label>
                            <Form.Control
                                type="date"
                                name="activeBetween.start"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.activeBetween.start || ''}
                                isInvalid={formik.touched.activeBetween?.start && !!formik.errors.activeBetween?.start}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.activeBetween?.start}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="endDate" >
                            <Form.Label>{i18n.t('jt_left_the.text')}</Form.Label>
                            <Form.Control
                                type="date"
                                name="activeBetween.end"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.activeBetween.end || ''}
                                isInvalid={formik.touched.activeBetween?.end && !!formik.errors.activeBetween?.end}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.activeBetween?.end}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Form.Group className="ml-auto mb-3" controlId="story-save">
                        <Button
                            variant="info"
                            fullWidth
                            type="submit"
                            data-cy="assign-agent-team-settings-info-btn">
                            {i18n.t('jt_save.text')}
                        </Button>
                    </Form.Group>
                </Row>
            </Form>
        </>
    );
});

export default AssignAgentTeamSettingsForm; 